import _ from 'underscore';

export type IUPAC =
  | 'A'
  | 'C'
  | 'G'
  | 'T'
  | 'U'
  | 'R'
  | 'Y'
  | 'M'
  | 'K'
  | 'W'
  | 'S'
  | 'B'
  | 'D'
  | 'H'
  | 'V'
  | 'N';
export type LOWERCASE_IUPAC =
  | 'a'
  | 'c'
  | 'g'
  | 't'
  | 'u'
  | 'r'
  | 'y'
  | 'm'
  | 'k'
  | 'w'
  | 's'
  | 'b'
  | 'd'
  | 'h'
  | 'v'
  | 'n';

export type DNA_IUPAC = Exclude<IUPAC, 'U'>;
export type DNA_LOWERCASE_IUPAC = Exclude<LOWERCASE_IUPAC, 'u'>;
export type DNA_IUPAC_ALL = DNA_IUPAC | DNA_LOWERCASE_IUPAC;
export type RNA_IUPAC = Exclude<IUPAC, 'T'>;
export type RNA_LOWERCASE_IUPAC = Exclude<LOWERCASE_IUPAC, 't'>;

export type StandardDNABase = 'A' | 'T' | 'C' | 'G';
export type StandardRNABase = 'A' | 'U' | 'C' | 'G';
export type StandardBase = StandardDNABase | StandardRNABase;

const UPPERCASE_DEGENERATE_BASES = ['V', 'H', 'D', 'B', 'S', 'W', 'K', 'M', 'Y', 'R', 'N'];
export const DEGENERATE_BASES = [
  ...UPPERCASE_DEGENERATE_BASES,
  ...UPPERCASE_DEGENERATE_BASES.map((base) => base.toLowerCase()),
];

export enum NucleotideType {
  DNA = 'DNA',
  RNA = 'RNA',
}

export enum ResidueType {
  NUCLEOTIDE = 'Nucleotide',
  AA = 'AA',
}

export type PolymerType = NucleotideType | ResidueType.AA;

export enum SequencePrime {
  FIVE = 'FIVE',
  THREE = 'THREE',
}

// Keep in sync with Notation in benchling/molbio/oligos/helpers.py.
export enum Notation {
  NATURAL_BASES = 'Natural bases',
  HELM = 'HELM',
  IDT = 'IDT',
  CUSTOM_SYNTAX = 'Custom notation',
}

export const GAP: '-' = '-';
export const GAP_PATTERN = new RegExp(GAP, 'g');

export enum MeltingTempMethod {
  primer3 = 'primer3', // oligotm derived SantaLucia 1998 thermodynamic parameters
  thermo = 'thermo', // thermo webtools tmc derived modified Breslauer parameters
  xia = 'xia', // RNA option, uses Xia parameters for RNA duplexes
}

export interface MeltingTempPreferences {
  method: MeltingTempMethod;
  dnaConc: number | null;
  monovalentConc: number | null;
  divalentConc: number | null;
  dntpConc: number | null;
}

export const MELTING_TEMP_PREFERENCES_DEFAULTS = {
  method: MeltingTempMethod.primer3,
  monovalentConc: 50,
  dnaConc: 250,
  divalentConc: 0,
  dntpConc: 0,
};

// these are the default values that the primer3 tool uses (see https://primer3.org/manual.html#globalTags)
export const MELTING_TEMP_PREFERENCES_PRIMER3_DEFAULTS: MeltingTempPreferences = {
  method: MeltingTempMethod.primer3,
  dnaConc: 50,
  monovalentConc: 50,
  divalentConc: 1.5,
  dntpConc: 0.6,
};

export const MELTING_TEMP_INITIAL_PREFERENCES_DNA = {
  method: MeltingTempMethod.primer3,
  dnaConc: null,
  monovalentConc: null,
  divalentConc: null,
  dntpConc: null,
};

export const MELTING_TEMP_INITIAL_PREFERENCES_RNA = {
  method: MeltingTempMethod.xia,
  dnaConc: null,
  monovalentConc: null,
  divalentConc: null,
  dntpConc: null,
};

export type BufferConditionKeys = keyof Omit<MeltingTempPreferences, 'method'>;

export interface EnergyDeltas {
  dH: number; // change in enthalpy in cal/mol
  dS: number; // change in entropy in cal/(mol * K)
}

export const TM_SANTALUCIA_DNA_NN_TERMINAL_BASES_TABLE: {[base in StandardDNABase | 'N']: EnergyDeltas} = {
  A: {dH: 2300, dS: 4.1},
  T: {dH: 2300, dS: 4.1},
  G: {dH: 100, dS: -2.8},
  C: {dH: 100, dS: -2.8},
  N: {dH: 1200, dS: 0.65}, // this is interpolated from the other values
};

// Nearest-neighbor data from SantaLucia (1998) table 2
// NOTE: terms for N seem to be interpolated as minimums dH and maximums for dS - this probably underestimates
// melting temp due to the formula used. An exception is the dS values for N which seem to be taken from the
// Breslauer averages instead
// NOTE: these values should be kept in sync with the backend (benchling/pybio/util.py)
export const TM_SANTALUCIA_DNA_NN_PARAMETERS: {
  [base in StandardDNABase | 'N']: {[base in StandardDNABase | 'N']: EnergyDeltas};
} = {
  A: {
    A: {dH: -7900, dS: -22.2},
    T: {dH: -7200, dS: -20.4},
    G: {dH: -7800, dS: -21.0},
    C: {dH: -8400, dS: -22.4},
    N: {dH: -7200, dS: -22.4},
  },
  T: {
    A: {dH: -7200, dS: -21.3},
    T: {dH: -7900, dS: -22.2},
    G: {dH: -8500, dS: -22.7},
    C: {dH: -8200, dS: -22.2},
    N: {dH: -7200, dS: -22.7},
  },
  G: {
    A: {dH: -8200, dS: -22.2},
    T: {dH: -8400, dS: -22.4},
    G: {dH: -8000, dS: -19.9},
    C: {dH: -9800, dS: -24.4},
    N: {dH: -8000, dS: -24.4},
  },
  C: {
    A: {dH: -8500, dS: -22.7},
    T: {dH: -7800, dS: -21.0},
    G: {dH: -10600, dS: -27.2},
    C: {dH: -8000, dS: -19.9},
    N: {dH: -7800, dS: -27.2},
  },
  N: {
    A: {dH: -7200, dS: -16.8},
    T: {dH: -7200, dS: -21.5},
    G: {dH: -7800, dS: -22.0},
    C: {dH: -8000, dS: -21.0},
    N: {dH: -7200, dS: -22.0},
  },
};

// Xia constants are from this paper: https://pubmed.ncbi.nlm.nih.gov/9778347/
// with some context in https://benchling.atlassian.net/wiki/spaces/MBMB/pages/242352358/Melting+Temperature+Tm+Notes
export const TM_XIA_RNA_NN_TERMINAL_BASES_TABLE: {
  [base in StandardRNABase | 'N']: EnergyDeltas;
} = {
  A: {dH: 3720, dS: 10.5},
  U: {dH: 3720, dS: 10.5},
  G: {dH: 0, dS: 0},
  C: {dH: 0, dS: 0},
  N: {dH: 1680, dS: 5.25}, // this is interpolated from the other values
};

// Nearest-neighbor data from Xia (1998) table 4
// Values for N are interpolated as simple averages
export const TM_XIA_RNA_NN_PARAMETERS: {
  [base in StandardRNABase | 'N']: {[base in StandardRNABase | 'N']: EnergyDeltas};
} = {
  A: {
    A: {dH: -6820, dS: -19.0},
    U: {dH: -9380, dS: -20.5},
    G: {dH: -10480, dS: -27.1},
    C: {dH: -11400, dS: -29.5},
    N: {dH: -9520, dS: -24.0},
  },
  U: {
    A: {dH: -7690, dS: -20.5},
    U: {dH: -6820, dS: -19.0},
    G: {dH: -10440, dS: -26.9},
    C: {dH: -12440, dS: -32.5},
    N: {dH: -9348, dS: -24.7},
  },
  G: {
    A: {dH: -12440, dS: -32.5},
    U: {dH: -11400, dS: -29.5},
    G: {dH: -13390, dS: -32.7},
    C: {dH: -14880, dS: -36.9},
    N: {dH: -13028, dS: -32.9},
  },
  C: {
    A: {dH: -10440, dS: -26.9},
    U: {dH: -10480, dS: -27.1},
    G: {dH: -10640, dS: -26.7},
    C: {dH: -13390, dS: -32.7},
    N: {dH: -11238, dS: -28.4},
  },
  N: {
    A: {dH: -9848, dS: -24.7},
    U: {dH: -9520, dS: -24.0},
    G: {dH: -11238, dS: -28.4},
    C: {dH: -13028, dS: -32.9},
    N: {dH: -10784, dS: -27.5},
  },
};

export const DNA_IUPAC_TO_BASES: {[code in DNA_IUPAC]: string} = {
  A: 'A',
  C: 'C',
  G: 'G',
  T: 'T',
  R: 'AG',
  Y: 'CT',
  M: 'AC',
  K: 'GT',
  W: 'AT',
  S: 'CG',
  B: 'CGT',
  D: 'AGT',
  H: 'ACT',
  V: 'ACG',
  N: 'ACGT',
};

export const RNA_IUPAC_TO_BASES: {[code in RNA_IUPAC]: string} = {
  A: 'A',
  C: 'C',
  G: 'G',
  U: 'U',
  R: 'AG',
  Y: 'CU',
  M: 'AC',
  K: 'GU',
  W: 'AU',
  S: 'CG',
  B: 'CGU',
  D: 'AGU',
  H: 'ACU',
  V: 'ACG',
  N: 'ACGU',
};

type IUPAC_TO_SUBSET_MAP = {
  [code in IUPAC]: Array<string>;
};

export const IUPAC_TO_SUBSET: IUPAC_TO_SUBSET_MAP = {
  A: ['A'],
  C: ['C'],
  G: ['G'],
  T: ['T'],
  U: ['U'],
  R: ['A', 'G', 'R'],
  Y: ['C', 'T', 'Y'],
  M: ['A', 'C', 'M'],
  K: ['G', 'T', 'K'],
  W: ['A', 'T', 'W'],
  S: ['C', 'G', 'S'],
  B: ['C', 'G', 'T', 'S', 'Y', 'K', 'B'],
  D: ['A', 'G', 'T', 'R', 'W', 'K', 'D'],
  H: ['A', 'C', 'T', 'M', 'W', 'Y', 'H'],
  V: ['A', 'C', 'G', 'M', 'R', 'S', 'V'],
  N: ['A', 'C', 'G', 'T', 'V', 'H', 'D', 'B', 'S', 'W', 'K', 'M', 'Y', 'R', 'N'],
};

export const IUPAC_TO_RNA_AGNOSTIC_SUBSET: IUPAC_TO_SUBSET_MAP = {
  ...IUPAC_TO_SUBSET,
  T: ['T', 'U'],
  U: ['U', 'T'],
  Y: ['C', 'T', 'U', 'Y'],
  K: ['G', 'T', 'U', 'K'],
  W: ['A', 'T', 'U', 'W'],
  B: ['C', 'G', 'T', 'U', 'S', 'Y', 'K', 'B'],
  D: ['A', 'G', 'T', 'U', 'R', 'W', 'K', 'D'],
  H: ['A', 'C', 'T', 'U', 'M', 'W', 'Y', 'H'],
  N: ['A', 'C', 'G', 'T', 'U', 'V', 'H', 'D', 'B', 'S', 'W', 'K', 'M', 'Y', 'R', 'N'],
};

type IUPAC_TO_REGEX_MAP = {
  [code in IUPAC]: string;
};

const buildRegexMap = (subsetMap: IUPAC_TO_SUBSET_MAP): IUPAC_TO_REGEX_MAP =>
  _.object(
    Object.entries(subsetMap).map(([base, subs]) => [
      base,
      subs.length === 1 ? subs[0] : `[${subs.join('')}]`,
    ])
  );

export const IUPAC_TO_REGEX: IUPAC_TO_REGEX_MAP = buildRegexMap(IUPAC_TO_SUBSET);

export const IUPAC_TO_RNA_AGNOSTIC_REGEX: IUPAC_TO_REGEX_MAP = buildRegexMap(IUPAC_TO_RNA_AGNOSTIC_SUBSET);

export const VALID_BASE_PATTERN = /^[ATGCUYRSWKMBDHVN]*$/i;

const DNA_UPPERCASE_COMPLEMENT_TABLE: {[code in DNA_IUPAC]: DNA_IUPAC} = {
  A: 'T',
  T: 'A',
  G: 'C',
  C: 'G',
  Y: 'R',
  R: 'Y',
  S: 'S',
  W: 'W',
  K: 'M',
  M: 'K',
  B: 'V',
  D: 'H',
  H: 'D',
  V: 'B',
  N: 'N',
};

const RNA_UPPERCASE_COMPLEMENT_TABLE: {[code in RNA_IUPAC]: RNA_IUPAC} = {
  A: 'U',
  U: 'A',
  G: 'C',
  C: 'G',
  Y: 'R',
  R: 'Y',
  S: 'S',
  W: 'W',
  K: 'M',
  M: 'K',
  B: 'V',
  D: 'H',
  H: 'D',
  V: 'B',
  N: 'N',
};

const DNA_LOWERCASE_COMPLEMENT_TABLE: {[code in DNA_LOWERCASE_IUPAC]: DNA_LOWERCASE_IUPAC} = {
  a: 't',
  t: 'a',
  g: 'c',
  c: 'g',
  y: 'r',
  r: 'y',
  s: 's',
  w: 'w',
  k: 'm',
  m: 'k',
  b: 'v',
  d: 'h',
  h: 'd',
  v: 'b',
  n: 'n',
};

const RNA_LOWERCASE_COMPLEMENT_TABLE: {[code in RNA_LOWERCASE_IUPAC]: RNA_LOWERCASE_IUPAC} = {
  a: 'u',
  u: 'a',
  g: 'c',
  c: 'g',
  y: 'r',
  r: 'y',
  s: 's',
  w: 'w',
  k: 'm',
  m: 'k',
  b: 'v',
  d: 'h',
  h: 'd',
  v: 'b',
  n: 'n',
};

export type IupacPartialMapping = Partial<Record<IUPAC | LOWERCASE_IUPAC, IUPAC | LOWERCASE_IUPAC>>;

export const DNA_COMPLEMENT_TABLE: {
  [code in DNA_LOWERCASE_IUPAC | DNA_IUPAC]: DNA_LOWERCASE_IUPAC | DNA_IUPAC;
} = {
  ...DNA_UPPERCASE_COMPLEMENT_TABLE,
  ...DNA_LOWERCASE_COMPLEMENT_TABLE,
};

export const RNA_COMPLEMENT_TABLE: {
  [code in RNA_LOWERCASE_IUPAC | RNA_IUPAC]: RNA_LOWERCASE_IUPAC | RNA_IUPAC;
} = {
  ...RNA_UPPERCASE_COMPLEMENT_TABLE,
  ...RNA_LOWERCASE_COMPLEMENT_TABLE,
};

export const DNA_COMPLEMENT_GAP_TABLE = {
  ...DNA_COMPLEMENT_TABLE,
  [GAP]: GAP,
};

export const RNA_COMPLEMENT_GAP_TABLE = {
  ...RNA_COMPLEMENT_TABLE,
  [GAP]: GAP,
};

export const NUCLEOTIDE_TYPE_TO_COMPLEMENT_TABLE: {
  [nucleotideType in NucleotideType]: IupacPartialMapping;
} = {
  [NucleotideType.DNA]: DNA_COMPLEMENT_TABLE,
  [NucleotideType.RNA]: RNA_COMPLEMENT_TABLE,
};

export const NUCLEOTIDE_TYPE_TO_COMPLEMENT_GAP_TABLE: {
  [nucleotideType in NucleotideType]: IupacPartialMapping;
} = {
  [NucleotideType.DNA]: DNA_COMPLEMENT_GAP_TABLE,
  [NucleotideType.RNA]: RNA_COMPLEMENT_GAP_TABLE,
};

// Data from Breslauer (1986) table 2. Values for degenerate bases are interpolated using simple averages
// NOTE: these values should be kept in sync with the backend (benchling/pybio/util.py)
export const TM_BRESLAUER_DNA_NN_PARAMETERS: {
  [base in DNA_IUPAC]: {[base in DNA_IUPAC]: EnergyDeltas};
} = {
  A: {
    A: {dH: -9100, dS: -24},
    T: {dH: -8600, dS: -23.9},
    G: {dH: -7800, dS: -20.8},
    C: {dH: -6500, dS: -17.3},
    R: {dH: -8450, dS: -22.4},
    Y: {dH: -7550, dS: -20.6},
    W: {dH: -8850, dS: -23.95},
    S: {dH: -7150, dS: -19.05},
    M: {dH: -7800, dS: -20.65},
    K: {dH: -8200, dS: -22.35},
    B: {dH: -7633.333, dS: -20.667},
    D: {dH: -8500, dS: -22.9},
    H: {dH: -8066.667, dS: -21.733},
    V: {dH: -7800, dS: -20.7},
    N: {dH: -8000, dS: -21.5},
  },
  T: {
    A: {dH: -6000, dS: -16.9},
    T: {dH: -9100, dS: -24},
    G: {dH: -5800, dS: -12.9},
    C: {dH: -5600, dS: -13.5},
    R: {dH: -5900, dS: -14.9},
    Y: {dH: -7350, dS: -18.75},
    W: {dH: -7550, dS: -20.45},
    S: {dH: -5700, dS: -13.2},
    M: {dH: -5800, dS: -15.2},
    K: {dH: -7450, dS: -18.45},
    B: {dH: -6833.333, dS: -16.8},
    D: {dH: -6966.667, dS: -17.933},
    H: {dH: -6900, dS: -18.133},
    V: {dH: -5800, dS: -14.433},
    N: {dH: -6625, dS: -16.825},
  },
  G: {
    A: {dH: -5600, dS: -13.5},
    T: {dH: -6500, dS: -17.3},
    G: {dH: -11000, dS: -26.6},
    C: {dH: -11100, dS: -26.7},
    R: {dH: -8300, dS: -20.05},
    Y: {dH: -8800, dS: -22},
    W: {dH: -6050, dS: -15.4},
    S: {dH: -11050, dS: -26.65},
    M: {dH: -8350, dS: -20.1},
    K: {dH: -8750, dS: -21.95},
    B: {dH: -9533.333, dS: -23.533},
    D: {dH: -7700, dS: -19.133},
    H: {dH: -7733.333, dS: -19.167},
    V: {dH: -9233.333, dS: -22.267},
    N: {dH: -8550, dS: -21.025},
  },
  C: {
    A: {dH: -5800, dS: -12.9},
    T: {dH: -7800, dS: -20.8},
    G: {dH: -11900, dS: -27.8},
    C: {dH: -11000, dS: -26.6},
    R: {dH: -8850, dS: -20.35},
    Y: {dH: -9400, dS: -23.7},
    W: {dH: -6800, dS: -16.85},
    S: {dH: -11450, dS: -27.2},
    M: {dH: -8400, dS: -19.75},
    K: {dH: -9850, dS: -24.3},
    B: {dH: -10233.333, dS: -25.067},
    D: {dH: -8500, dS: -20.5},
    H: {dH: -8200, dS: -20.1},
    V: {dH: -9566.667, dS: -22.433},
    N: {dH: -9125, dS: -22.025},
  },
  R: {
    A: {dH: -7350, dS: -18.75},
    T: {dH: -7550, dS: -20.6},
    G: {dH: -9400, dS: -23.7},
    C: {dH: -8800, dS: -22},
    R: {dH: -8375, dS: -21.225},
    Y: {dH: -8175, dS: -21.3},
    W: {dH: -7450, dS: -19.675},
    S: {dH: -9100, dS: -22.85},
    M: {dH: -8075, dS: -20.375},
    K: {dH: -8475, dS: -22.15},
    B: {dH: -8583.333, dS: -22.1},
    D: {dH: -8100, dS: -21.017},
    H: {dH: -7900, dS: -20.45},
    V: {dH: -8516.667, dS: -21.483},
    N: {dH: -8275, dS: -21.262},
  },
  Y: {
    A: {dH: -5900, dS: -14.9},
    T: {dH: -8450, dS: -22.4},
    G: {dH: -8850, dS: -20.35},
    C: {dH: -8300, dS: -20.05},
    R: {dH: -7375, dS: -17.625},
    Y: {dH: -8375, dS: -21.225},
    W: {dH: -7175, dS: -18.65},
    S: {dH: -8575, dS: -20.2},
    M: {dH: -7100, dS: -17.475},
    K: {dH: -8650, dS: -21.375},
    B: {dH: -8533.333, dS: -20.933},
    D: {dH: -7733.333, dS: -19.217},
    H: {dH: -7550, dS: -19.117},
    V: {dH: -7683.333, dS: -18.433},
    N: {dH: -7875, dS: -19.425},
  },
  W: {
    A: {dH: -7550, dS: -20.45},
    T: {dH: -8850, dS: -23.95},
    G: {dH: -6800, dS: -16.85},
    C: {dH: -6050, dS: -15.4},
    R: {dH: -7175, dS: -18.65},
    Y: {dH: -7450, dS: -19.675},
    W: {dH: -8200, dS: -22.2},
    S: {dH: -6425, dS: -16.125},
    M: {dH: -6800, dS: -17.925},
    K: {dH: -7825, dS: -20.4},
    B: {dH: -7233.333, dS: -18.733},
    D: {dH: -7733.333, dS: -20.417},
    H: {dH: -7483.333, dS: -19.933},
    V: {dH: -6800, dS: -17.567},
    N: {dH: -7312.5, dS: -19.162},
  },
  S: {
    A: {dH: -5700, dS: -13.2},
    T: {dH: -7150, dS: -19.05},
    G: {dH: -11450, dS: -27.2},
    C: {dH: -11050, dS: -26.65},
    R: {dH: -8575, dS: -20.2},
    Y: {dH: -9100, dS: -22.85},
    W: {dH: -6425, dS: -16.125},
    S: {dH: -11250, dS: -26.925},
    M: {dH: -8375, dS: -19.925},
    K: {dH: -9300, dS: -23.125},
    B: {dH: -9883.333, dS: -24.3},
    D: {dH: -8100, dS: -19.817},
    H: {dH: -7966.667, dS: -19.633},
    V: {dH: -9400, dS: -22.35},
    N: {dH: -8837.5, dS: -21.525},
  },
  M: {
    A: {dH: -7450, dS: -18.45},
    T: {dH: -8200, dS: -22.35},
    G: {dH: -9850, dS: -24.3},
    C: {dH: -8750, dS: -21.95},
    R: {dH: -8650, dS: -21.375},
    Y: {dH: -8475, dS: -22.15},
    W: {dH: -7825, dS: -20.4},
    S: {dH: -9300, dS: -23.125},
    M: {dH: -8100, dS: -20.2},
    K: {dH: -9025, dS: -23.325},
    B: {dH: -8933.333, dS: -22.867},
    D: {dH: -8500, dS: -21.7},
    H: {dH: -8133.333, dS: -20.917},
    V: {dH: -8683.333, dS: -21.567},
    N: {dH: -8562.5, dS: -21.762},
  },
  K: {
    A: {dH: -5800, dS: -15.2},
    T: {dH: -7800, dS: -20.65},
    G: {dH: -8400, dS: -19.75},
    C: {dH: -8350, dS: -20.1},
    R: {dH: -7100, dS: -17.475},
    Y: {dH: -8075, dS: -20.375},
    W: {dH: -6800, dS: -17.925},
    S: {dH: -8375, dS: -19.925},
    M: {dH: -7075, dS: -17.65},
    K: {dH: -8100, dS: -20.2},
    B: {dH: -8183.333, dS: -20.167},
    D: {dH: -7333.333, dS: -18.533},
    H: {dH: -7316.667, dS: -18.65},
    V: {dH: -7516.667, dS: -18.35},
    N: {dH: -7587.5, dS: -18.925},
  },
  B: {
    A: {dH: -5800, dS: -14.433},
    T: {dH: -7800, dS: -20.7},
    G: {dH: -9566.667, dS: -22.433},
    C: {dH: -9233.333, dS: -22.267},
    R: {dH: -7683.333, dS: -18.433},
    Y: {dH: -8516.667, dS: -21.483},
    W: {dH: -6800, dS: -17.567},
    S: {dH: -9400, dS: -22.35},
    M: {dH: -7516.667, dS: -18.35},
    K: {dH: -8683.333, dS: -21.567},
    B: {dH: -8866.667, dS: -21.8},
    D: {dH: -7722.222, dS: -19.189},
    H: {dH: -7611.111, dS: -19.133},
    V: {dH: -8200, dS: -19.711},
    N: {dH: -8100, dS: -19.958},
  },
  D: {
    A: {dH: -6900, dS: -18.133},
    T: {dH: -8066.667, dS: -21.733},
    G: {dH: -8200, dS: -20.1},
    C: {dH: -7733.333, dS: -19.167},
    R: {dH: -7550, dS: -19.117},
    Y: {dH: -7900, dS: -20.45},
    W: {dH: -7483.333, dS: -19.933},
    S: {dH: -7966.667, dS: -19.633},
    M: {dH: -7316.667, dS: -18.65},
    K: {dH: -8133.333, dS: -20.917},
    B: {dH: -8000, dS: -20.333},
    D: {dH: -7722.222, dS: -19.989},
    H: {dH: -7566.667, dS: -19.678},
    V: {dH: -7611.111, dS: -19.133},
    N: {dH: -7725, dS: -19.783},
  },
  H: {
    A: {dH: -6966.667, dS: -17.933},
    T: {dH: -8500, dS: -22.9},
    G: {dH: -8500, dS: -20.5},
    C: {dH: -7700, dS: -19.133},
    R: {dH: -7733.333, dS: -19.217},
    Y: {dH: -8100, dS: -21.017},
    W: {dH: -7733.333, dS: -20.417},
    S: {dH: -8100, dS: -19.817},
    M: {dH: -7333.333, dS: -18.533},
    K: {dH: -8500, dS: -21.7},
    B: {dH: -8233.333, dS: -20.844},
    D: {dH: -7988.889, dS: -20.444},
    H: {dH: -7722.222, dS: -19.989},
    V: {dH: -7722.222, dS: -19.189},
    N: {dH: -7916.667, dS: -20.117},
  },
  V: {
    A: {dH: -6833.333, dS: -16.8},
    T: {dH: -7633.333, dS: -20.667},
    G: {dH: -10233.333, dS: -25.067},
    C: {dH: -9533.333, dS: -23.533},
    R: {dH: -8533.333, dS: -20.933},
    Y: {dH: -8583.333, dS: -22.1},
    W: {dH: -7233.333, dS: -18.733},
    S: {dH: -9883.333, dS: -24.3},
    M: {dH: -8183.333, dS: -20.167},
    K: {dH: -8933.333, dS: -22.867},
    B: {dH: -9133.333, dS: -23.089},
    D: {dH: -8233.333, dS: -20.844},
    H: {dH: -8000, dS: -20.333},
    V: {dH: -8866.667, dS: -21.8},
    N: {dH: -8558.333, dS: -21.517},
  },
  N: {
    A: {dH: -6625, dS: -16.825},
    T: {dH: -8000, dS: -21.5},
    G: {dH: -9125, dS: -22.025},
    C: {dH: -8550, dS: -21.025},
    R: {dH: -7875, dS: -19.425},
    Y: {dH: -8275, dS: -21.263},
    W: {dH: -7312.5, dS: -19.162},
    S: {dH: -8837.5, dS: -21.525},
    M: {dH: -7587.5, dS: -18.925},
    K: {dH: -8562.5, dS: -21.762},
    B: {dH: -8558.333, dS: -21.517},
    D: {dH: -7916.667, dS: -20.117},
    H: {dH: -7725, dS: -19.783},
    V: {dH: -8100, dS: -19.958},
    N: {dH: -8075, dS: -20.344},
  },
};

export const PREPARED_OD260_NN_DATA: {[code in DNA_IUPAC]: {[code in DNA_IUPAC]: number}} = {
  A: {
    A: 27400,
    T: 22800,
    G: 25000,
    C: 21200,
    R: 26200,
    Y: 22000,
    W: 25100,
    S: 23100,
    M: 24300,
    K: 23900,
    B: 23000,
    D: 25066.666666666668,
    H: 23800,
    V: 24533.333333333332,
    N: 24100,
  },
  T: {
    A: 23400,
    T: 16800,
    G: 19000,
    C: 16200,
    R: 21200,
    Y: 16500,
    W: 20100,
    S: 17600,
    M: 19800,
    K: 17900,
    B: 17333.333333333332,
    D: 19733.333333333332,
    H: 18800,
    V: 19533.333333333332,
    N: 18850,
  },
  G: {
    A: 25200,
    T: 20000,
    G: 21600,
    C: 17600,
    R: 23400,
    Y: 18800,
    W: 22600,
    S: 19600,
    M: 21400,
    K: 20800,
    B: 19733.333333333332,
    D: 22266.666666666668,
    H: 20933.333333333332,
    V: 21466.666666666668,
    N: 21100,
  },
  C: {
    A: 21200,
    T: 15200,
    G: 18000,
    C: 14600,
    R: 19600,
    Y: 14900,
    W: 18200,
    S: 16300,
    M: 17900,
    K: 16600,
    B: 15933.333333333334,
    D: 18133.333333333332,
    H: 17000,
    V: 17933.333333333332,
    N: 17250,
  },
  R: {
    A: 26300,
    T: 21400,
    G: 23300,
    C: 19400,
    R: 24800,
    Y: 20400,
    W: 23850,
    S: 21350,
    M: 22850,
    K: 22350,
    B: 21366.666666666668,
    D: 23666.666666666668,
    H: 22366.666666666668,
    V: 23000,
    N: 22600,
  },
  Y: {
    A: 22300,
    T: 16000,
    G: 18500,
    C: 15400,
    R: 20400,
    Y: 15700,
    W: 19150,
    S: 16950,
    M: 18850,
    K: 17250,
    B: 16633.333333333332,
    D: 18933.333333333332,
    H: 17900,
    V: 18733.333333333332,
    N: 18050,
  },
  W: {
    A: 25400,
    T: 19800,
    G: 22000,
    C: 18700,
    R: 23700,
    Y: 19250,
    W: 22600,
    S: 20350,
    M: 22050,
    K: 20900,
    B: 20166.666666666668,
    D: 22400,
    H: 21300,
    V: 22033.333333333332,
    N: 21475,
  },
  S: {
    A: 23200,
    T: 17600,
    G: 19800,
    C: 16100,
    R: 21500,
    Y: 16850,
    W: 20400,
    S: 17950,
    M: 19650,
    K: 18700,
    B: 17833.333333333332,
    D: 20200,
    H: 18966.666666666668,
    V: 19700,
    N: 19175,
  },
  M: {
    A: 24300,
    T: 19000,
    G: 21500,
    C: 17900,
    R: 22900,
    Y: 18450,
    W: 21650,
    S: 19700,
    M: 21100,
    K: 20250,
    B: 19466.666666666668,
    D: 21600,
    H: 20400,
    V: 21233.333333333332,
    N: 20675,
  },
  K: {
    A: 24300,
    T: 18400,
    G: 20300,
    C: 16900,
    R: 22300,
    Y: 17650,
    W: 21350,
    S: 18600,
    M: 20600,
    K: 19350,
    B: 18533.333333333332,
    D: 21000,
    H: 19866.666666666668,
    V: 20500,
    N: 19975,
  },
  B: {
    A: 23266.666666666668,
    T: 17333.333333333332,
    G: 19533.333333333332,
    C: 16133.333333333334,
    R: 21400,
    Y: 16733.333333333332,
    W: 20300,
    S: 17833.333333333332,
    M: 19700,
    K: 18433.333333333332,
    B: 17666.666666666668,
    D: 20044.444444444445,
    H: 18911.11111111111,
    V: 19644.444444444445,
    N: 19066.666666666668,
  },
  D: {
    A: 25333.333333333332,
    T: 19866.666666666668,
    G: 21866.666666666668,
    C: 18333.333333333332,
    R: 23600,
    Y: 19100,
    W: 22600,
    S: 20100,
    M: 21833.333333333332,
    K: 20866.666666666668,
    B: 20022.222222222223,
    D: 22355.555555555555,
    H: 21177.777777777777,
    V: 21844.444444444445,
    N: 21350,
  },
  H: {
    A: 24000,
    T: 18266.666666666668,
    G: 20666.666666666668,
    C: 17333.333333333332,
    R: 22333.333333333332,
    Y: 17800,
    W: 21133.333333333332,
    S: 19000,
    M: 20666.666666666668,
    K: 19466.666666666668,
    B: 18755.555555555555,
    D: 20977.777777777777,
    H: 19866.666666666668,
    V: 20666.666666666668,
    N: 20066.666666666668,
  },
  V: {
    A: 24600,
    T: 19333.333333333332,
    G: 21533.333333333332,
    C: 17800,
    R: 23066.666666666668,
    Y: 18566.666666666668,
    W: 21966.666666666668,
    S: 19666.666666666668,
    M: 21200,
    K: 20433.333333333332,
    B: 19555.555555555555,
    D: 21822.222222222223,
    H: 20577.777777777777,
    V: 21311.11111111111,
    N: 20816.666666666668,
  },
  N: {
    A: 24300,
    T: 18700,
    G: 20900,
    C: 17400,
    R: 22600,
    Y: 18050,
    W: 21500,
    S: 19150,
    M: 20850,
    K: 19800,
    B: 19000,
    D: 21300,
    H: 20133.333333333332,
    V: 20866.666666666668,
    N: 20325,
  },
};

export const PREPARED_OD260_INDIVIDUAL_DATA: {[code in DNA_IUPAC]: number} = {
  A: 15400,
  T: 8700,
  G: 11500,
  C: 7400,
  R: 13450,
  Y: 8050,
  W: 12050,
  S: 9450,
  M: 11400,
  K: 10100,
  B: 9200,
  D: 11866.667,
  H: 10500,
  V: 11433.333,
  N: 10750,
};

/*
 * Keep in sync with the constant in benchling/pybio/util.py.
 * Three tables are intentionally omitted because they include ambiguous codons.
 */
export enum CodonTranslationTable {
  STANDARD = 'STANDARD',
  VERTEBRATE_MITOCHONDRIAL = 'VERTEBRATE_MITOCHONDRIAL',
  YEAST_MITOCHONDRIAL = 'YEAST_MITOCHONDRIAL',
  MOLD_PROTOZOAN_COELENTERATE_MITOCHONDRIAL_MYCOPLASMA_SPIROPLASMA = 'MOLD_PROTOZOAN_COELENTERATE_MITOCHONDRIAL_MYCOPLASMA_SPIROPLASMA',
  INVERTEBRATE_MITOCHONDRIAL = 'INVERTEBRATE_MITOCHONDRIAL',
  CILIATE_DASYCLADACEAN_HEXAMITA_NUCLEAR = 'CILIATE_DASYCLADACEAN_HEXAMITA_NUCLEAR',
  ECHINODERM_FLATWORM_MITOCHONDRIAL = 'ECHINODERM_FLATWORM_MITOCHONDRIAL',
  EUPLOTID_NUCLEAR = 'EUPLOTID_NUCLEAR',
  BACTERIAL_ARCHAEAL_PLANT_PLASTID = 'BACTERIAL_ARCHAEAL_PLANT_PLASTID',
  ALTERNATIVE_YEAST_NUCLEAR = 'ALTERNATIVE_YEAST_NUCLEAR',
  ASCIDIAN_MITOCHONDRIAL = 'ASCIDIAN_MITOCHONDRIAL',
  ALTERNATIVE_FLATWORM_MITOCHONDRIAL = 'ALTERNATIVE_FLATWORM_MITOCHONDRIAL',
  CHLOROPHYCEAN_MITOCHONDRIAL = 'CHLOROPHYCEAN_MITOCHONDRIAL',
  TREMATODE_MITOCHONDRIAL = 'TREMATODE_MITOCHONDRIAL',
  SCENEDESMUS_OBLIQUUS_MITOCHONDRIAL = 'SCENEDESMUS_OBLIQUUS_MITOCHONDRIAL',
  THRAUSTOCHYTRIUM_MITOCHONDRIAL = 'THRAUSTOCHYTRIUM_MITOCHONDRIAL',
  RHABDOPLEURIDAE_MITOCHONDRIAL = 'RHABDOPLEURIDAE_MITOCHONDRIAL',
  CANDIDATE_DIVISION_SR1_GRACILIBACTERIA = 'CANDIDATE_DIVISION_SR1_GRACILIBACTERIA',
  PACHYSOLEN_TANNOPHILUS_NUCLEAR = 'PACHYSOLEN_TANNOPHILUS_NUCLEAR',
  // KARYORELICT_NUCLEAR = 'KARYORELICT_NUCLEAR',
  // CONDYLOSTOMA_NUCLEAR = 'CONDYLOSTOMA_NUCLEAR',
  MESODINIUM_NUCLEAR = 'MESODINIUM_NUCLEAR',
  PERITRICH_NUCLEAR = 'PERITRICH_NUCLEAR',
  // BLASTOCRITHIDIA_NUCLEAR = 'BLASTOCRITHIDIA_NUCLEAR',
  CEPHALODISCIDAE_MITOCHONDRIAL_UAA_TYR = 'CEPHALODISCIDAE_MITOCHONDRIAL_UAA_TYR',
}

export const GENETIC_CODE_USER_PREFERENECE_KEY = 'MOLBIO_GENETIC_CODE';

export const CODON_LENGTH = 3;
